// src/components/myAccount/pages/EnhancedSubscriptionsPage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  Crown,
  Shield,
  Users,
  Tag,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle,
  CreditCard,
  ArrowRight
} from 'lucide-react';
import stripeService from '../../../services/stripeService';
import subscriptionPlansService from '../../../services/subscriptionPlansService';
import { subscriptionHelpers } from '../../../services/graphqlSubscriptions';
import { RefreshCw } from 'lucide-react';

const EnhancedSubscriptionsPage = ({ user, userRoles, onRefreshData }) => {
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingPlanType, setLoadingPlanType] = useState(null);
  const [finalizingSession, setFinalizingSession] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeValid, setPromoCodeValid] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [hasCompletedTrial, setHasCompletedTrial] = useState(false);

  // Fetch plans from API
  const fetchPlans = useCallback(async () => {
      try {
        setLoadingPlans(true);
        setError(''); // Clear any previous errors
        
        // Fetch plans with frontend_only=true to get only plans marked for frontend display
        const response = await subscriptionPlansService.getPlans(true);
        
        console.log('Fetched plans response:', response);
        
        if (response.success && response.plans && response.plans.length > 0) {
          // Use features from database if available, otherwise build from product_restrictions
          const plansWithFeatures = response.plans.map(plan => {
            let features = [];
            
            // First, try to use the features array from the database (what AdminHQ uses)
            if (plan.features && Array.isArray(plan.features) && plan.features.length > 0) {
              features = plan.features;
            } else {
              // Fall back to building features from product_restrictions
              let restrictions = {};
              if (plan.product_restrictions) {
                if (typeof plan.product_restrictions === 'string') {
                  try {
                    restrictions = JSON.parse(plan.product_restrictions);
                  } catch (e) {
                    console.warn('Failed to parse product_restrictions for plan', plan.name, e);
                    restrictions = {};
                  }
                } else {
                  restrictions = plan.product_restrictions;
                }
              }
              
              // Build features from restrictions
              // Equity
              if (restrictions.equity?.enabled) {
                const equity = restrictions.equity;
                features.push(`Equity: ${equity.max_companies === -1 ? 'unlimited' : equity.max_companies} ${equity.max_companies === 1 ? 'company' : 'companies'} • ${equity.max_shareholders === -1 ? 'unlimited' : equity.max_shareholders} shareholders`);
              } else {
                features.push('Equity: not included');
              }
              
              // Dataroom
              if (restrictions.dataroom?.enabled) {
                const dataroom = restrictions.dataroom;
                let dataroomFeature = `Dataroom: ${dataroom.max_projects === -1 ? 'unlimited' : dataroom.max_projects} ${dataroom.max_projects === 1 ? 'project' : 'projects'}`;
                if (dataroom.max_items_per_project !== undefined) {
                  const itemsLimit = dataroom.max_items_per_project === -1 ? 'unlimited items' : `${dataroom.max_items_per_project} items`;
                  dataroomFeature += ` • ${itemsLimit} per project`;
                }
                if (dataroom.max_data_gb !== undefined) {
                  const dataLimit = dataroom.max_data_gb === -1 ? 'unlimited storage' : `${dataroom.max_data_gb} GB storage`;
                  dataroomFeature += ` • ${dataLimit}`;
                }
                features.push(dataroomFeature);
              } else {
                features.push('Dataroom: not included');
              }
              
              // Questions
              if (restrictions.questions?.enabled) {
                const questions = restrictions.questions;
                if (questions.max_questions === -1) {
                  features.push('Questions: no limits');
                } else {
                  features.push(`Questions: ${questions.max_questions} questions`);
                }
              } else {
                features.push('Questions: not included');
              }
              
              // CapRaise
              if (restrictions.capraise?.enabled) {
                const capraise = restrictions.capraise;
                features.push(`CapRaise: ${capraise.max_deals === -1 ? 'unlimited' : capraise.max_deals} ${capraise.max_deals === 1 ? 'deal' : 'deals'}`);
              } else {
                features.push('CapRaise: not included');
              }
              
              // Investor CRM
              if (restrictions.investor_crm?.enabled) {
                const investorCrm = restrictions.investor_crm;
                if (investorCrm.max_contacts === -1) {
                  features.push('Investor CRM: unlimited contacts');
                } else {
                  features.push(`Investor CRM: ${investorCrm.max_contacts} contacts`);
                }
              } else {
                features.push('Investor CRM: not included');
              }
              
              // Analysis
              if (restrictions.analysis?.enabled) {
                const analysis = restrictions.analysis;
                const accessLevel = analysis.data_access || 'limited';
                features.push(`Analysis: ${accessLevel} data access`);
              } else {
                features.push('Analysis: not included');
              }
            }
            
            return {
              ...plan,
              features,
              // Ensure trial_days is a number
              trial_days: plan.trial_days || 0
            };
          });
          
          console.log('Plans with features:', plansWithFeatures);
          setSubscriptionPlans(plansWithFeatures);
          setError(''); // Clear any errors on success
        } else {
          // No plans returned from API
          console.warn('No plans returned from API:', response);
          if (response.error) {
            setError(`Unable to load subscription plans: ${response.error}`);
          } else {
            setError('No subscription plans available. Please contact support.');
          }
          // Only use fallback if absolutely necessary
          setSubscriptionPlans([]);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
        setError(`Failed to load subscription plans: ${err.message}`);
        // Only use fallback in development or as last resort
        if (process.env.NODE_ENV === 'development') {
          console.warn('Using fallback plans in development mode');
          setSubscriptionPlans(getDefaultPlans());
        } else {
          setSubscriptionPlans([]);
        }
      } finally {
        setLoadingPlans(false);
        setIsRefreshing(false);
        setLastUpdated(new Date());
      }
  }, []);

  // Check if user has completed a trial period
  const checkTrialCompletion = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { supabase } = await import('../../../config');
      
      // Get all subscriptions for this user to check trial history
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plan_type, status, trial_ends_at, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error checking trial completion:', error);
        return;
      }
      
      if (data && data.length > 0) {
        // Check if user has ever had a trial that has ended
        // or if they currently have a paid subscription (which means they likely completed a trial)
        const now = new Date();
        const hasCompletedTrialCheck = data.some(sub => {
          // If they have a paid active subscription (not trial), they likely completed a trial
          if (sub.status === 'active' && sub.plan_type !== 'trial' && sub.plan_type !== 'free') {
            return true;
          }
          // If they had a trial that has ended
          if (sub.trial_ends_at && new Date(sub.trial_ends_at) < now) {
            return true;
          }
          // If they had a trial status that's no longer active
          if (sub.status === 'trial' && sub.trial_ends_at && new Date(sub.trial_ends_at) < now) {
            return true;
          }
          return false;
        });
        
        setHasCompletedTrial(hasCompletedTrialCheck);
      }
    } catch (err) {
      console.error('Error checking trial completion:', err);
    }
  }, [user?.id]);

  // Fetch current subscription from database
  const fetchCurrentSubscription = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { supabase } = await import('../../../config');
      
      // First, try to get the most recent subscription (any status)
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plan_type, status, trial_ends_at, stripe_subscription_id, cancelled_at, expires_at, billing_cycle, amount_cents')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching subscription:', error);
        setCurrentSubscription(null);
        return;
      }
      
      // Filter to only show active/trial subscriptions (not canceled)
      if (data && data.status && !['canceled'].includes(data.status)) {
        setCurrentSubscription(data);
      } else {
        setCurrentSubscription(null);
      }
    } catch (err) {
      console.error('Error fetching current subscription:', err);
      setCurrentSubscription(null);
    }
  }, [user?.id]);

  // Handle cancel subscription
  const handleCancelSubscription = async () => {
    if (!currentSubscription?.stripe_subscription_id) {
      setError('No active subscription found to cancel.');
      return;
    }

    setCancellingSubscription(true);
    setError('');
    setMessage('');

    try {
      const response = await stripeService.cancelSubscription(currentSubscription.stripe_subscription_id);
      
      if (response.success) {
        setMessage('Subscription canceled successfully. You will retain access until the end of your billing period.');
        setShowCancelConfirm(false);
        // Refresh subscription data
        await fetchCurrentSubscription();
        if (onRefreshData) {
          await onRefreshData();
        }
      } else {
        setError(response.error || 'Failed to cancel subscription. Please try again or contact support.');
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Load plans and subscription on mount
  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
    checkTrialCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlans, fetchCurrentSubscription, checkTrialCompletion, user?.id]);

  // Refresh subscription when userRoles change (e.g., after payment)
  useEffect(() => {
    if (user?.id) {
      fetchCurrentSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles, user?.id, fetchCurrentSubscription]);

  // Subscribe to real-time updates from subscription_plans table
  useEffect(() => {
    const subscriptionId = subscriptionHelpers.subscribeToSubscriptionPlans((payload) => {
      console.log('Subscription plan changed:', payload);
      
      // Refresh plans when any change occurs (INSERT, UPDATE, DELETE)
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE' || payload.eventType === 'DELETE') {
        console.log('Subscription plan updated, refreshing...');
        fetchPlans();
      }
    });

    // Cleanup subscription on unmount
    return () => {
      if (subscriptionId) {
        const { graphQLSubscriptions } = require('../../../services/graphqlSubscriptions');
        graphQLSubscriptions.unsubscribe(subscriptionId);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchPlans();
  };

  // Default plans fallback
  const getDefaultPlans = () => {
    return [
      {
        id: 1,
        name: 'Free',
        description: 'Great for exploring the platform basics',
        price_monthly: 0,
        price_yearly: 0,
        features: [
          'Equity: 1 company • up to 5 shareholders',
          'Dataroom: 3 projects • 50 items per project • 1 GB storage',
          'Questions: limited usage',
          'CapRaise: not included',
          'Investor CRM: not included',
          'Analysis: limited access'
        ],
        plan_type: 'free',
        is_active: true,
        trial_days: 0,
        sort_order: 1
      },
      {
        id: 2,
        name: 'Basic',
        description: 'Core tools for teams getting started',
        price_monthly: 3900,
        price_yearly: 39000,
        features: [
          'Equity: up to 2 companies • 20 shareholders',
          'Dataroom: 10 projects • 200 items per project • 10 GB storage',
          'Questions: moderate limits',
          'CapRaise: up to 3 deals',
          'Investor CRM: 50 contacts',
          'Analysis: standard data access'
        ],
        plan_type: 'basic',
        is_active: true,
        trial_days: 14,
        sort_order: 2
      },
      {
        id: 3,
        name: 'Pro',
        description: 'Expanded access for growing teams',
        price_monthly: 7900,
        price_yearly: 79000,
        features: [
          'Equity: up to 5 companies • 50 shareholders',
          'Dataroom: 20 projects • unlimited items per project • unlimited storage',
          'Questions: no limits',
          'CapRaise: up to 5 deals',
          'Investor CRM: unlimited contacts',
          'Analysis: extended data access'
        ],
        plan_type: 'pro',
        is_active: true,
        trial_days: 14,
        sort_order: 3
      }
    ];
  };

  // Sample promo codes
  const samplePromoCodes = [
    { code: 'WELCOME20', description: '20% off first month' },
    { code: '30DAYFREE', description: '30-day free trial' },
    { code: 'SAVE50', description: '$50 off annual plans' }
  ];

  const hasSubscription = () => {
    return userRoles && userRoles.length > 0;
  };

  const getCurrentPlan = () => {
    // First check database subscription (most reliable)
    if (currentSubscription && currentSubscription.plan_type) {
      const plan = subscriptionPlans.find(p => p.plan_type === currentSubscription.plan_type);
      if (plan) return plan;
    }
    
    // Fallback to userRoles
    if (hasSubscription()) {
      const currentRole = userRoles.find(role => role.role === 'creator');
      if (currentRole) {
        const planType = currentRole.plan || currentRole.plan_type || 'basic';
        return subscriptionPlans.find(plan => plan.plan_type === planType);
      }
    }
    
    return null;
  };

  const isCurrentPlan = (planType) => {
    // Check database subscription first
    if (currentSubscription && currentSubscription.plan_type === planType) {
      return true;
    }
    
    // Fallback to userRoles
    const current = getCurrentPlan();
    return current && current.plan_type === planType;
  };

  // Determine if a plan is an upgrade or downgrade
  const getPlanAction = (plan) => {
    if (!currentSubscription) return 'subscribe'; // No subscription = subscribe
    
    const currentPlanType = currentSubscription.plan_type;
    const targetPlanType = plan.plan_type;
    
    // Plan hierarchy
    const planHierarchy = {
      'free': 0,
      'basic': 1,
      'pro': 2,
      'enterprise': 3
    };
    
    const currentLevel = planHierarchy[currentPlanType] || 0;
    const targetLevel = planHierarchy[targetPlanType] || 0;
    
    if (currentLevel === targetLevel) {
      return 'current'; // Same plan
    } else if (targetLevel > currentLevel) {
      return 'upgrade'; // Higher tier
    } else {
      return 'downgrade'; // Lower tier
    }
  };

  const formatPrice = (cents, planType = null, cycle = billingCycle) => {
    // Show "Custom" for Enterprise plans with zero price
    if (cents === 0 && planType === 'enterprise') {
      return 'Custom';
    }
    // Show "Free" for other plans with zero price
    if (cents === 0) return 'Free';
    const amount = cycle === 'yearly' ? cents : cents;
    return `$${(amount / 100).toFixed(2)}`;
  };

  const getYearlySavings = (monthlyPrice) => {
    if (monthlyPrice === 0) return 0;
    const monthlyTotal = monthlyPrice * 12;
    const yearlyPrice = monthlyPrice * 10; // Assuming 2 months free for yearly
    return Math.round((monthlyTotal - yearlyPrice) / 100);
  };

  const validatePromoCode = async (code) => {
    if (!code.trim()) {
      setPromoCodeValid(null);
      return;
    }
    
    setValidatingPromo(true);
    try {
      // Use real API validation
      const validation = await stripeService.validatePromoCode(code, null);
      
      if (validation.valid) {
        setPromoCodeValid({
          valid: true,
          code: validation.promo_code.code,
          type: validation.promo_code.type,
          value: validation.promo_code.value,
          description: `Discount applied: ${validation.promo_code.type === 'percentage' ? validation.promo_code.value + '%' : '$' + (validation.promo_code.value / 100).toFixed(2)}`
        });
        setError('');
      } else {
        setPromoCodeValid({
          valid: false,
          message: validation.error || 'Invalid promo code'
        });
        setError(validation.error || 'Invalid promo code');
      }
    } catch (err) {
      setPromoCodeValid({
        valid: false,
        message: err.message || 'Error validating code'
      });
      setError(err.message || 'Error validating promo code');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handlePromoCodeSubmit = (e) => {
    e.preventDefault();
    validatePromoCode(promoCode);
  };

  const handleSubscribe = async (plan) => {
    if (!user?.id) {
      setError('Please log in to subscribe.');
      return;
    }

    if (plan.plan_type === 'free') {
      setMessage('You are currently viewing the free plan. Upgrade anytime for additional features.');
      return;
    }

    // Check if user is trying to start a trial but has already completed one
    if (plan.trial_days > 0 && hasCompletedTrial) {
      setError('You have already used your free trial period. Please subscribe to a paid plan.');
      return;
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    if (!origin) {
      setError('Unable to determine redirect URLs. Please try again in your browser.');
      return;
    }

    setLoading(true);
    setLoadingPlanType(plan.plan_type);
    setMessage('');
    setError('');

    try {
      // Re-validate promo code with plan ID if one is entered
      let validatedPromo = null;
      if (promoCode && promoCode.trim()) {
        try {
          const validation = await stripeService.validatePromoCode(promoCode, plan.id);
          if (validation.valid) {
            validatedPromo = {
              valid: true,
              code: validation.promo_code.code,
              id: validation.promo_code.id,
              type: validation.promo_code.type,
              value: validation.promo_code.value
            };
          } else {
            setError(validation.error || 'Invalid promo code for this plan');
            setLoading(false);
            setLoadingPlanType(null);
            return;
          }
        } catch (err) {
          setError(err.message || 'Error validating promo code');
          setLoading(false);
          setLoadingPlanType(null);
          return;
        }
      }

      const session = await stripeService.createCheckoutSession({
        user_id: user.id,
        planId: plan.id,
        planType: plan.plan_type,
        billingCycle,
        promoCode: validatedPromo,
        success_url: `${origin}/my-account/subscriptions?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${origin}/my-account/subscriptions`
      });

      // Check if subscription was updated instead of creating new checkout
      if (session?.updated) {
        // Subscription was updated successfully
        setMessage(session.message || `Successfully ${session.subscription?.plan_type === plan.plan_type ? 'updated' : 'upgraded'} to ${plan.name || plan.plan_type} plan!`);
        
        // Refresh subscription data
        if (onRefreshData) {
          onRefreshData();
        }
        
        // Refresh the page to show updated subscription
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (session?.url) {
        // New subscription - redirect to Stripe checkout
        window.location.href = session.url;
      } else {
        throw new Error('Unable to start checkout session.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while starting checkout.');
    } finally {
      setLoading(false);
      setLoadingPlanType(null);
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'free': return Users;
      case 'basic': return Shield;
      case 'pro': return Crown;
      default: return CreditCard;
    }
  };

  const getPlanColor = (planType) => {
    switch (planType) {
      case 'free': return 'text-gray-600';
      case 'basic': return 'text-blue-600';
      case 'pro': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  // const getPlanBgColor = (planType) => {
  //   switch (planType) {
  //     case 'free': return 'border-gray-200';
  //     case 'basic': return 'border-blue-200';
  //     case 'pro': return 'border-purple-200';
  //     default: return 'border-gray-200';
  //   }
  // }; // Unused

  useEffect(() => {
    if (!sessionId || !user?.id) return;

    let isCancelled = false;

    const finalizeSession = async () => {
      setFinalizingSession(true);
      setMessage('');
      setError('');

      try {
        const response = await stripeService.getCheckoutSession(sessionId, user.id);

        if (response?.success) {
          setMessage('Subscription activated! Your access has been updated.');
          // Refresh user roles and subscription data
          if (onRefreshData) {
            await onRefreshData();
          }
          // Also refresh subscription directly
          await fetchCurrentSubscription();
          // Refresh plans to update current plan indicators
          await fetchPlans();
        } else {
          setError('We could not verify your subscription. Please contact support if this persists.');
        }
      } catch (err) {
        setError(err.message || 'Failed to verify subscription. Please try again.');
      } finally {
        if (!isCancelled) {
          setFinalizingSession(false);
          if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            params.delete('session_id');
            setSearchParams(params, { replace: true });
          }
        }
      }
    };

    finalizeSession();

    return () => {
      isCancelled = true;
    };
  }, [sessionId, user?.id, onRefreshData, setSearchParams, fetchCurrentSubscription, fetchPlans]);

  if (loadingPlans) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading subscription plans...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1"></div>
          <div className="flex-1 text-center">
            <h1 className="text-4xl font-bold text-gray-900">
              Choose Your Plan
            </h1>
          </div>
          <div className="flex-1 flex justify-end">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loadingPlans}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh subscription plans"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        <p className="text-xl text-gray-600 mb-8">
          Select the perfect plan for your business needs. All plans include access to all our products.
        </p>
        {lastUpdated && (
          <p className="text-sm text-gray-500 -mt-6 mb-2">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        )}

        {/* Billing Toggle */}
        <div className="flex items-center justify-center mb-8">
          <span className={`mr-3 ${billingCycle === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`ml-3 ${billingCycle === 'yearly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
            Yearly
          </span>
          {billingCycle === 'yearly' && (
            <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
              Save up to 17%
            </span>
          )}
        </div>
      </div>

      {/* Promo Code Section */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          <button
            onClick={() => setShowPromoForm(!showPromoForm)}
            className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            <Tag className="h-4 w-4 mr-2" />
            Have a promo code?
          </button>
        </div>

        {showPromoForm && (
          <div className="max-w-md mx-auto mt-4">
            <form onSubmit={handlePromoCodeSubmit} className="flex space-x-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Enter promo code"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={validatingPromo || !promoCode.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {validatingPromo ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Apply'
                )}
              </button>
            </form>

            {promoCodeValid && (
              <div className="mt-2 text-center">
                {promoCodeValid.valid ? (
                  <div className="flex items-center justify-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {promoCodeValid.description}
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-red-600">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {promoCodeValid.message}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 mb-2">Popular codes:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {samplePromoCodes.map((promo) => (
                  <button
                    key={promo.code}
                    onClick={() => setPromoCode(promo.code)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-gray-700"
                  >
                    {promo.code}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      {finalizingSession && (
        <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded flex items-center justify-center">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Completing your subscription...
        </div>
      )}
      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}
      {message && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center justify-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {message}
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Upgrade/Downgrade Action Buttons - Show above current subscription info */}
      {currentSubscription && 
       currentSubscription.status !== 'canceled' && 
       subscriptionPlans.length > 0 && 
       subscriptionPlans.some(plan => !isCurrentPlan(plan.plan_type)) && (
        <div className="mb-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
            Change Your Plan
          </h3>
          <p className="text-sm text-gray-500 mb-4 text-center">
            Upgrade or downgrade your subscription. Changes take effect immediately with prorated billing.
          </p>
          <div className="flex flex-nowrap justify-center gap-3 overflow-x-auto">
            {subscriptionPlans
              .filter(plan => !isCurrentPlan(plan.plan_type))
              .map(plan => {
                const action = getPlanAction(plan);
                const isPlanLoading = loading && loadingPlanType === plan.plan_type;
                
                return (
                  <button
                    key={plan.id}
                    onClick={() => handleSubscribe(plan)}
                    disabled={isPlanLoading || loading || finalizingSession}
                    className={`px-6 py-3 rounded-lg font-medium text-sm transition-all flex items-center whitespace-nowrap ${
                      action === 'upgrade'
                        ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                        : 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isPlanLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <span className="mr-2">{action === 'upgrade' ? '↑' : '↓'}</span>
                        <div className="text-left">
                          <div className="font-medium">
                            {action === 'upgrade' ? 'Upgrade' : 'Downgrade'} to {plan.name}
                          </div>
                          <div className="text-xs opacity-75">
                            {billingCycle === 'yearly' 
                              ? `$${(plan.price_yearly / 100).toFixed(2)}/year`
                              : `$${(plan.price_monthly / 100).toFixed(2)}/month`
                            }
                          </div>
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Current Subscription Management */}
      {currentSubscription && currentSubscription.status !== 'canceled' && (
        <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Subscription</h3>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Plan:</span> {currentSubscription.plan_type ? currentSubscription.plan_type.charAt(0).toUpperCase() + currentSubscription.plan_type.slice(1) : 'N/A'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Status:</span> {currentSubscription.status === 'trial' || currentSubscription.status === 'trialing' ? 'Trial' : 'Active'}
                </p>
                {currentSubscription.status === 'canceled' && (
                  <p className="text-sm text-orange-600 font-medium">
                    ⚠️ Subscription will cancel at end of billing period
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              {currentSubscription.status !== 'canceled' && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Subscription?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? You will retain access to all features until the end of your current billing period. After that, your subscription will be canceled and you'll lose access to premium features.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={cancellingSubscription}
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {cancellingSubscription ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  'Yes, Cancel Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      {subscriptionPlans.length === 0 && !loadingPlans ? (
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Plans Available</h3>
          <p className="text-gray-600 mb-4">
            {error || 'Subscription plans are not currently available. Please contact support.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-12">
          {subscriptionPlans.map((plan) => {
          const isPlanLoading = loading && loadingPlanType === plan.plan_type;
          const IconComponent = getPlanIcon(plan.plan_type);
          const currentPrice = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
          const yearlySavings = billingCycle === 'yearly' ? getYearlySavings(plan.price_monthly) : 0;
          const isCurrent = isCurrentPlan(plan.plan_type);
          const isOtherPlanLoading = loading && loadingPlanType && loadingPlanType !== plan.plan_type;
          const isDisabled = isPlanLoading || isOtherPlanLoading || finalizingSession || isCurrent;
          
          return (
            <div
              key={plan.id}
              className={`bg-white rounded-xl border-2 p-6 relative transition-all hover:shadow-lg border-gray-200 ${isCurrent ? 'ring-2 ring-green-200' : ''} flex flex-col`}
            >
              {/* Current Plan Badge */}
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Current Plan
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <IconComponent className={`h-12 w-12 mx-auto mb-4 ${getPlanColor(plan.plan_type)}`} />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                
                <div className="mb-4">
                  <div className="text-4xl font-bold text-gray-900">
                    {formatPrice(currentPrice, plan.plan_type)}
                  </div>
                  {currentPrice > 0 && (
                    <div className="text-gray-600">
                      /{billingCycle === 'yearly' ? 'year' : 'month'}
                    </div>
                  )}
                  {currentPrice === 0 && plan.plan_type === 'enterprise' && (
                    <div className="text-gray-600 text-sm mt-1">
                      Contact us for pricing
                    </div>
                  )}
                  {yearlySavings > 0 && (
                    <div className="text-sm text-green-600 font-medium">
                      Save ${yearlySavings}/year
                    </div>
                  )}
                </div>

                {plan.trial_days > 0 && !hasCompletedTrial && (
                  <div className="flex items-center justify-center text-sm text-blue-600 mb-4">
                    <Calendar className="h-4 w-4 mr-1" />
                    {plan.trial_days} day free trial
                  </div>
                )}
                {plan.trial_days > 0 && hasCompletedTrial && (
                  <div className="flex items-center justify-center text-sm text-gray-500 mb-4">
                    <Calendar className="h-4 w-4 mr-1" />
                    Trial not available (already used)
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="mt-auto">
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={isDisabled}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                    isCurrent
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : isPlanLoading || isOtherPlanLoading
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : getPlanAction(plan) === 'upgrade'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : getPlanAction(plan) === 'downgrade'
                      ? 'bg-orange-600 text-white hover:bg-orange-700'
                      : plan.plan_type === 'pro'
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : plan.plan_type === 'basic'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-600 text-white hover:bg-gray-700'
                  }`}
                >
                  {isPlanLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : (
                    <>
                      {getPlanAction(plan) === 'upgrade' && '⬆️ Upgrade'}
                      {getPlanAction(plan) === 'downgrade' && '⬇️ Downgrade'}
                      {getPlanAction(plan) === 'subscribe' && (
                        <>
                          Get Started
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* FAQ Section */}
      <div className="bg-gray-50 rounded-lg p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          Frequently Asked Questions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Can I change plans anytime?</h4>
            <p className="text-gray-600 text-sm">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h4>
            <p className="text-gray-600 text-sm">
              We accept all major credit cards and process payments securely through Stripe.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Is there a free trial?</h4>
            <p className="text-gray-600 text-sm">
              Yes! Basic plans include a 14-day free trial, and Pro plans include a 30-day free trial.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h4>
            <p className="text-gray-600 text-sm">
              Absolutely. You can cancel your subscription at any time with no cancellation fees.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedSubscriptionsPage;
